import { FolderIcon, UserIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PROJECT_LIST } from "../../routes";
import { IoMdArrowRoundBack } from "react-icons/io";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import {
  CATEGORY_TYPES,
  PROJECT_TYPES,
  REQUIRED_FIELDS,
} from "./constant";
import { useProjects } from "./Hooks/useProjects";
import { useClient } from "../ClientMaster/Hooks/useClient";
import { Spinner } from "../../Components/Common";
import SearchableDropdown from "../../Components/Common/SearchableDropdown";

const CreateProject = () => {
  const { createProject, loading } = useProjects({ fetch: false });
  const { fetchAllClients, } = useClient();
  const navigate = useNavigate();
  const [client, setClient] = useState({});

  const initialValues = {
    name: "",
    estimated_start_date: "",
    estimated_end_date: "",
    category: "",
    type: "",
    client_id: "",
  };
  
  const validationSchema = Yup.object({
    name: Yup.string().required(REQUIRED_FIELDS.PROJECT_NAME),
    estimated_start_date: Yup.date().nullable(),
    estimated_end_date: Yup.date()
      .nullable()
      .min(
        Yup.ref("estimated_start_date"),
        "End date cannot be before start date"
      ),
    category: Yup.string().required(REQUIRED_FIELDS.CATEGORY),
    type: Yup.string().required(REQUIRED_FIELDS.TYPE),
    client_id: Yup.string().required(REQUIRED_FIELDS.CLIENT_NAME),
  });

  const handleSubmit = async (data) => {
    await createProject(data);
    navigate(PROJECT_LIST);
  }

  const handleSelectClient = (client, setFieldValue) => {
    setClient(client);
    setFieldValue("client_id", client?.id || ""); 
  };

  return (
    <div className="mx-2 my-1 md:my-2">
      <div className="flex flex-row items-center justify-between gap-4 mx-2 my-6">
        <div className="flex justify-start gap-4 items-center">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-primary text-center text-sm font-lg"
          >
            <IoMdArrowRoundBack size={20} className="text-primary" />
          </button>
          <h1 className="text-xl md:text-2xl text-center text-secondary font-semibold">
            Create New Project
          </h1>
        </div>
      </div>

      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validateOnChange={true}
        validateOnBlur={false}
      >
        {({ setFieldValue }) => (
          <Form className="mx-2">
            {/* Sections */}
            <div className="grid grid-cols-1 lg:grid-cols-2 py-4 mb-2">
              {/* Project Details */}
              <div className="pr-0 md:pr-8 border-gray-200 lg:border-r">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <FolderIcon className="w-6 h-6 text-secondary" />
                  <h2 className="text-xl font-semibold text-secondary">
                    Project Details
                  </h2>
                </div>
                <div className="space-y-4">
                  {/* Field Wrapper */}
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Project Name <sub className="text-red-500 top-0">*</sub>
                    </label>
                    <Field
                      name="name"
                      type="text"
                      placeholder="Enter project name"
                      className="border-b border-gray-300 px-3 py-2 text-sm focus:ring-1 focus:outline-none focus:border-blue-500 focus:ring-blue-200"
                      disabled={loading}
                    />
                    <ErrorMessage
                      name="name"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div className="relative flex flex-col gap-1">
                      <label className="text-sm font-medium text-gray-700">
                        Type <sub className="text-red-500 top-0">*</sub>
                      </label>
                      <Field
                        as="select"
                        name="type"
                        className="border border-gray-300 px-3 py-2 text-sm focus:ring-1  focus:border-blue-500 focus:ring-blue-200 focus:outline-none"
                        disabled={loading}
                      >
                        <option value="">Select type</option>
                        {PROJECT_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="type"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-sm font-medium text-gray-700">
                        Category <sub className="text-red-500 top-0">*</sub>
                      </label>
                      <Field
                        as="select"
                        name="category"
                        className="border border-gray-300 px-3 py-2 text-sm focus:ring-1  focus:border-blue-500 focus:ring-blue-200 focus:outline-none"
                        disabled={loading}
                      >
                        <option value="">Select type</option>
                        {CATEGORY_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="category"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div className="flex flex-col gap-1">
                      <label className="text-sm font-medium text-gray-700">
                        Estimated Start Date
                      </label>
                      <Field
                        name="estimated_start_date"
                        type="date"
                        className="border border-gray-300 px-3 py-2 text-sm focus:ring-1  focus:border-blue-500 focus:ring-blue-200 focus:outline-none"
                        disabled={loading}
                      />
                      <ErrorMessage
                        name="estimated_start_date"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-sm font-medium text-gray-700">
                        Estimated End Date
                      </label>
                      <Field
                        name="estimated_end_date"
                        type="date"
                        className="border border-gray-300  px-3 py-2 text-sm focus:ring-1  focus:border-blue-500 focus:ring-blue-200 focus:outline-none"
                        disabled={loading}
                      />
                      <ErrorMessage
                        name="estimated_end_date"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Client Details */}
              <div className="pl-0 lg:pl-8 border-gray-200 lg:border-l mt-8 md:mt-0">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <UserIcon className="w-6 h-6 text-secondary" />
                  <h2 className="text-xl font-medium text-secondary">
                    Client Details
                  </h2>
                </div>
                <div className="space-y-3">
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Client Name <sub className="text-red-500 top-0">*</sub>
                    </label>
                    <SearchableDropdown
                      fetchFunction={fetchAllClients}
                      onSelect={(client) => handleSelectClient(client, setFieldValue)}
                      labelKey="name"
                      placeholder="Select client"
                    />
                    <ErrorMessage
                      name="client_id"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>
                      <div className="grid grid-cols-2 gap-6">
                      <div className="flex flex-col gap-1">
                        <p className="text-sm font-medium text-gray-700 mt-1.5">Contact Person Name</p>
                      <p
                        className={`font-sm text-gray-700 ${client?.contact_person_name ? "" : "italic"
                          }`}
                      >
                              {client?.contact_person_name || "Select contact person name"}
                            </p>
                      </div>
                      <div className="flex flex-col gap-1">
                        <p className="text-sm font-medium text-gray-700 mt-1.5">Mobile</p>
                        <p
                          className={`font-sm text-gray-700 ${client?.mobile ? "" : "italic"
                            }`}
                        >
                          {client?.mobile || "Select mobile"}
                        </p>
                      </div>

                      </div>
                      <div className="col-span-2 mt-3">
                    <p className="text-sm font-medium text-gray-700 mt-4">Address</p>
                    <p
                      className={`font-sm text-gray-700 ${client?.address ? "" : "italic"}`}
                          >
                            {client?.address || "Select address"}
                          </p>
                      </div>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-4 border-gray-200">
              <button
                type="submit"
                className="px-5 py-2  bg-primary text-white flex justify-center shadow hover:shadow-lg hover:bg-secondary active:scale-95 transition-all text-sm font-medium"
                disabled={loading}
              >
                {loading ? <Spinner /> : "Save Project"}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default CreateProject;